/**
 * <s-carousel> — single-row product carousel with previous/next buttons.
 *
 * Required markup:
 *   [data-carousel-track]  — horizontal scroll-snap list holding the slides.
 *   [data-carousel-prev]   — button that scrolls back one page of slides.
 *   [data-carousel-next]   — button that scrolls forward one page of slides.
 *
 * The track stays natively swipeable/scrollable; buttons scroll by one visible
 * page and are disabled at either end so they never do nothing.
 */
class SCarousel extends HTMLElement {
  #ac;
  #frame = 0;
  #resizeObserver;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.#page(-1), opts);
    this.next?.addEventListener('click', () => this.#page(1), opts);
    this.track.addEventListener('scroll', () => this.#queueUpdate(), { ...opts, passive: true });
    // ResizeObserver also fires when a hidden tab panel becomes visible, so buttons are correct on reveal.
    this.#resizeObserver = new ResizeObserver(() => this.#queueUpdate());
    this.#resizeObserver.observe(this.track);

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #page(direction) {
    // RTL tracks report negative scrollLeft; scrollBy with a signed inline delta handles both.
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const delta = this.track.clientWidth * direction * (isRtl ? -1 : 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #queueUpdate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);
    const atStart = position <= 1;
    const atEnd = position >= max - 1;
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
    this.toggleAttribute('data-scrollable', max > 1);
  }
}

if (!customElements.get('s-carousel')) {
  customElements.define('s-carousel', SCarousel);
}
